import { useState, useEffect } from 'react';
import { useOneAgruparCache } from './useOneAgruparCache';

export function useToolsState() {
  const [isReady, setIsReady] = useState<boolean>(false);
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [showRunPsConfirm, setShowRunPsConfirm] = useState(false);
  const [showRefreshConfirm, setShowRefreshConfirm] = useState(false);
  const [showCloseDocumentConfirm, setShowCloseDocumentConfirm] = useState(false);
  const [showOneRegua, setShowOneRegua] = useState(false);
  const [showOneBitmap, setShowOneBitmap] = useState(false);
  const [showOneBitmapV1, setShowOneBitmapV1] = useState(false);
  const [showOneAgrupar, setShowOneAgrupar] = useState(false);
  const [showMontaTech, setShowMontaTech] = useState(false);
  const [showMoldeOne, setShowMoldeOne] = useState(false);
  const [showTestSmb, setShowTestSmb] = useState(false);
  const [showOneResize, setShowOneResize] = useState(false);
  const [showOneContorno, setShowOneContorno] = useState(false);
  const [oneAgruparRunning, setOneAgruparRunning] = useState(false);
  // Usar hook de cache para OneAgrupar (carregamento sob demanda)
  const oneAgruparCache = useOneAgruparCache();
  const [showCollectMetadata, setShowCollectMetadata] = useState(false);
  const [collectMetadataResult, setCollectMetadataResult] = useState<any>(null);
  const [showQuotationCalculator, setShowQuotationCalculator] = useState(false);
  // Chave de instância do orçamento: incrementada a cada abertura via atalho para
  // forçar remontagem limpa (usada como `key` no render). Blindagem da ferramenta "7".
  const [quotationInstanceKey, setQuotationInstanceKey] = useState(0);
  const [showExportProducaoOffline, setShowExportProducaoOffline] = useState(false);
  const [runPsDontAsk, setRunPsDontAsk] = useState<boolean>(() => {
    try { return window.localStorage.getItem('cdr:tools:runps:noAsk') === 'true'; } catch { return false; }
  });
  const [appVersion, setAppVersion] = useState<string | null>(null);
  const [minAddonVersion, setMinAddonVersion] = useState<string>('0.0.23');
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [busyToolIds, setBusyToolIds] = useState<Set<string>>(() => new Set());

  // Carregar versão mínima do localStorage
  useEffect(() => {
    try {
      const savedMin = window.localStorage.getItem('cdr:addon:minVersion');
      if (savedMin) setMinAddonVersion(savedMin);
    } catch {}
  }, []);

  // Os dados da ferramenta OneAgrupar são carregados automaticamente pelo hook useOneAgruparCache

  // Função para fechar todos os modais
  const closeAllModals = () => {
    setShowRunPsConfirm(false);
    setShowRefreshConfirm(false);
    setShowCloseDocumentConfirm(false);
    setShowOneRegua(false);
    setShowOneBitmap(false);
    setShowOneBitmapV1(false);
    setShowOneAgrupar(false);
    setShowMontaTech(false);
    setShowMoldeOne(false);
    setShowTestSmb(false);
    setShowOneResize(false);
    setShowOneContorno(false);
    setCollectMetadataResult(null);
    setShowQuotationCalculator(false);
    setShowExportProducaoOffline(false);
  };

  // Função para limpar cache da ferramenta OneAgrupar
  const clearOneAgruparCache = () => {
    oneAgruparCache.clearCache();
  };

  // Abre a ferramenta de orçamento garantindo remontagem limpa mesmo se a flag já
  // estiver true (evita no-op silencioso ao apertar o atalho "7"). A key de instância
  // muda a cada chamada, forçando o React a remontar QuotationCalculatorPage.
  const openQuotationCalculator = () => {
    setShowQuotationCalculator(true);
    setQuotationInstanceKey((k) => k + 1);
  };

  const markBusy = (toolId: string) => {
    setBusyToolIds((prev) => {
      const next = new Set(prev);
      next.add(toolId);
      return next;
    });
  };

  const unmarkBusy = (toolId: string) => {
    setBusyToolIds((prev) => {
      const next = new Set(prev);
      next.delete(toolId);
      return next;
    });
  };

  return {
    // Estados
    isReady,
    sendingId,
    showRunPsConfirm,
    showRefreshConfirm,
    showCloseDocumentConfirm,
    showOneRegua,
    showOneBitmap,
    showOneBitmapV1,
    showOneAgrupar,
    showMontaTech,
    showMoldeOne,
    showTestSmb,
    showOneResize,
    showOneContorno,
    oneAgruparRunning,
    oneAgruparToolData: oneAgruparCache.toolData,
    showCollectMetadata,
    collectMetadataResult,
    showQuotationCalculator,
    quotationInstanceKey,
    showExportProducaoOffline,
    runPsDontAsk,
    appVersion,
    minAddonVersion,
    showShortcuts,
    errorMessage,
    
    // Setters
    setIsReady,
    setSendingId,
    setShowRunPsConfirm,
    setShowRefreshConfirm,
    setShowCloseDocumentConfirm,
    setShowOneRegua,
    setShowOneBitmap,
    setShowOneBitmapV1,
    setShowOneAgrupar,
    setShowMontaTech,
    setShowMoldeOne,
    setShowTestSmb,
    setShowOneResize,
    setShowOneContorno,
    setOneAgruparRunning,
    setOneAgruparToolData: () => {}, // Não usado mais, dados vêm do cache
    setShowCollectMetadata,
    setCollectMetadataResult,
    setShowQuotationCalculator,
    setShowExportProducaoOffline,
    setRunPsDontAsk,
    setAppVersion,
    setMinAddonVersion,
    setShowShortcuts,
    setErrorMessage,
    
    // Funções utilitárias
    closeAllModals,
    clearOneAgruparCache,
    openQuotationCalculator,
    busyToolIds,
    markBusy,
    unmarkBusy,
    
    // Hook do OneAgrupar para carregamento sob demanda
    oneAgruparCache
  };
}
